import { createFileRoute } from "@tanstack/react-router";
import { getChainLabel } from "~/features/asset-domain";
// Deep-import the pure param helpers instead of the `~/features/symbol` barrel:
// this route file is eager (statically pulled by routeTree.gen), and the barrel
// re-exports the trade-ticket/trade-routes surface which drags viem into the
// initial bundle. The heavy graph is still loaded lazily via the dynamic
// `import("~/features/symbol/symbol-route")` in the loader below.
import {
  normalizeSymbolRouteChainId,
  normalizeSymbolRoutePathParam,
} from "~/features/symbol/symbol-route-params";
import { createSocialHeadMeta } from "~/lib/social-head";
import { createSymbolSocialMetadata } from "~/lib/social-metadata";

export function parseChainScopedSymbolRouteParams(rawParams: {
  chainId: string;
  symbol: string;
}): { chainId: string; symbol: string } {
  const chainId = normalizeSymbolRouteChainId(rawParams.chainId);
  const symbol = normalizeSymbolRoutePathParam(rawParams.symbol);
  if (!chainId || !symbol) {
    return false as never;
  }

  return { chainId, symbol };
}

export const Route = createFileRoute("/_app/symbol/$chainId/$symbol")({
  params: {
    priority: 20,
    parse: parseChainScopedSymbolRouteParams,
  },
  loader: ({ context, params, preload }) => {
    // Never gate navigation on data: preload may wait for the code chunk only
    // (see canonical-symbol-route-options.ts for the full rationale).
    const moduleTask = import("~/features/symbol/symbol-route");
    moduleTask
      .then(({ preloadSymbolRoute }) => preloadSymbolRoute(context, params))
      .catch(() => {
        // Warmup is best-effort; the page's own queries surface real errors.
      });

    if (preload) {
      return moduleTask.then(() => undefined);
    }
  },
  head: ({ params }) => ({
    meta: [
      ...createSocialHeadMeta(
        createSymbolSocialMetadata(params.symbol, {
          title: `${params.symbol} on ${
            getChainLabel(params.chainId) ?? params.chainId
          } | Glider`,
        }),
      ),
      { content: "noindex", name: "robots" },
    ],
  }),
});
